<template>
  <div class="mx-4 flex flex-col gap-2">
    <section class="flex w-full flex-wrap items-center gap-2">
      <ButtonTertiary
        v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
        action="filter"
        class="rounded-full!"
        data-test="notifications-filter"
        :hide_text="true"
        @onClick="toggle_filter = !toggle_filter"
      />

      <ButtonTertiary
        action="sort"
        data-test="notification-sorting-dropdown"
        :hide_text="is_narrow || mq.isMobile"
        @onClick="toggle_popover"
      >
        {{ $t(`sorting.${sorting_label}`) }}
      </ButtonTertiary>

      <ToggleToken
        data-test="show-unopen-notifications"
        :is_toggled="q_is_unread_only"
        @onClick="q_is_unread_only = !q_is_unread_only"
      >
        {{ $t('notification.labels.unreadOnly') }}
      </ToggleToken>

      <Popover ref="popover">
        <div class="flex flex-col gap-0.5 p-1">
          <div
            class="cursor-pointer rounded-md px-2.5 py-2 text-sm hover:bg-selected"
            data-test="sort-updated-at-desc"
            @click="$emit('set_sort_order', ['updated_at desc'])"
          >
            {{ $t('sorting.updated_at_desc') }}
          </div>
          <div
            class="cursor-pointer rounded-md px-2.5 py-2 text-sm hover:bg-selected"
            data-test="sort-updated-at-asc"
            @click="$emit('set_sort_order', ['updated_at asc'])"
          >
            {{ $t('sorting.updated_at_asc') }}
          </div>

          <div
            class="cursor-pointer rounded-md px-2.5 py-2 text-sm hover:bg-selected"
            data-test="sort-level-name-asc"
            @click="$emit('set_sort_order', ['notification_level_type desc', 'notification_level_name asc'])"
          >
            {{ is_sup ? $t('sorting.notification_level_name_asc_sup') : $t('sorting.notification_level_name_asc') }}
          </div>

          <div
            class="cursor-pointer rounded-md px-2.5 py-2 text-sm hover:bg-selected"
            data-test="sort-level-name-desc"
            @click="$emit('set_sort_order', ['notification_level_type desc', 'notification_level_name desc'])"
          >
            {{ is_sup ? $t('sorting.notification_level_name_desc_sup') : $t('sorting.notification_level_name_desc') }}
          </div>
        </div>
      </Popover>

      <a
        v-tooltip.bottom="$t('notification.actions.markAllAsRead')"
        class="flex size-8 cursor-pointer items-center justify-center rounded-full bg-surface-100 hover:bg-surface-200"
        data-test="mark-all-as-read"
        @click.stop="$emit('open_notification_subgroups_all')"
      >
        <AppIcon name="regular-eye" />
      </a>
    </section>

    <section
      v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE && !toggle_filter"
      class="flex grow flex-wrap items-center gap-2"
    >
      <FilterTag
        v-if="q_project_id && project_selected"
        data-test="project-tag-filter"
        @close="q_project_id = null"
      >
        {{ $truncate(project_selected.attributes.name, 20) }}
      </FilterTag>
      <FilterTag
        v-if="unit_selected"
        data-test="unit-tag-filter"
        @close="[(q_unit_id = null)]"
      >
        {{ $truncate(unit_selected.attributes.name, 20) }}
      </FilterTag>
      <FilterTag
        v-if="q_decision_type_id && decision_type_selected"
        data-test="decisions-tag-filter"
        @close="q_decision_type_id = null"
      >
        <FallbackTranslation
          :record="decision_type_selected"
          field="name"
          :truncate="20"
        />
      </FilterTag>
    </section>

    <section v-if="toggle_filter">
      <div class="relative mb-4">
        <div
          v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
          class="flex w-full gap-2"
        >
          <div class="flex-1">
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('ticket.labels.project') }}</label>
            <SelectProject
              :active="project_selected"
              @changeSelect="(p) => (q_project_id = _.get(p, 'id', null))"
            />
          </div>
          <div
            class="flex-1"
            v-if="!is_sup"
          >
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('ticket.labels.unit') }}</label>
            <SelectUnit
              :active="unit_selected"
              :disabled="!selectable_units"
              :project_id="q_project_id"
              @changeSelect="(u) => (q_unit_id = _.get(u, 'id', null))"
            />
          </div>
        </div>
        <template v-if="$flipper.enabled($FT.DECISIONS_CORE) && current_profile.type === PROFILE_TYPES.EMPLOYEE">
          <a
            class="mt-2 flex justify-end text-xs"
            data-test="show-extra-filters"
            @click.stop="show_extra_filters = !show_extra_filters"
          >
            {{ $t(`notification.labels.${show_extra_filters ? 'hideExtraFilters' : 'showExtraFilters'}`) }}
          </a>
          <div v-if="show_extra_filters">
            <label class="mb-1 block text-sm font-medium text-gray-700">{{ $t('decision.labels.decisions') }}</label>
            <SelectDecisionType
              :model-value="decision_type_selected"
              :project_id="q_project_id"
              :unit_id="q_unit_id"
              @changeSelect="(dt) => (q_decision_type_id = _.get(dt, 'id', null))"
            />
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import _ from 'lodash-es';
import { useStore } from 'vuex';
import { useMqService } from '@/plugins/mq';
import { PROFILE_TYPES, PROJECT_TYPES } from '@/app/data/model_constants';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';
import { FT } from '@/app/data/flipper_constants';
import ToggleToken from '@/app/shared_components/buttons/ToggleToken.vue';
import FilterTag from '@/app/shared_components/tags/FilterTag.vue';
import FallbackTranslation from '@/app/shared_components/FallbackTranslation.vue';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import SelectUnit from '@/app/shared_components/selects/SelectUnit.vue';
import SelectDecisionType from '@/app/shared_components/selects/SelectDecisionType.vue';
import INJECT_KEYS from '@/app/data/inject_keys';
import { useToasts } from '@/app/util/toasts.js';

const props = defineProps({
  sort_order: { type: String, required: true },
  sort_order_type: { type: String, required: true },
});

const emit = defineEmits(['set_sort_order', 'fetch_notification_subgroups', 'open_notification_subgroups_all']);

const store = useStore();
const mq = useMqService();
const $flipper = inject(INJECT_KEYS.FLIPPER);
const $FT = FT;
const { toast_action_failed } = useToasts();

const initialized = ref(false);
const q_is_unread_only = ref(false);
const q_project_id = ref(null);
const q_unit_id = ref(null);
const q_decision_type_id = ref(null);
const toggle_filter = ref(false);
const show_extra_filters = ref(false);
const popover = ref(null);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const decision_types = computed(() => store.getters.decision_types);
const sidebar_prefs = computed(() => store.getters.sidebar_prefs);
const projects = computed(() => store.getters.projects);
const sidebar_type = computed(() => store.getters.sidebar_type);
const units = computed(() => store.getters.units);

const is_narrow = computed(() => (sidebar_prefs.value.sidebar_width || 400) < 450);
const selectable_units = computed(() => Boolean(q_project_id.value));

const filters = computed(() => {
  const decision_type_id = q_decision_type_id.value;
  const project_id = q_project_id.value;
  const unread = q_is_unread_only.value;
  const unit_id = q_unit_id.value;
  return {
    decision_type_id,
    project_id,
    unit_id,
    unread,
  };
});

const project_selected = computed(() => {
  if (!q_project_id.value) return null;
  return _.find(projects.value, ['id', q_project_id.value]);
});

const is_sup = computed(() => current_client.value.attributes.project_type === PROJECT_TYPES.SUP);

const unit_selected = computed(() => {
  if (!q_unit_id.value) return null;
  return _.find(units.value, ['id', q_unit_id.value]);
});

const decision_type_selected = computed(() => {
  if (!q_decision_type_id.value) return null;
  return _.find(decision_types.value, ['id', q_decision_type_id.value]);
});

const sorting_label = computed(() => [props.sort_order_type, props.sort_order].join('_'));

const FETCH_DECISION_TYPE = (payload) => store.dispatch('FETCH_DECISION_TYPE', payload);
const UPDATE_SIDEBAR_PREFS = (payload) => store.dispatch('UPDATE_SIDEBAR_PREFS', payload);
const FETCH_PROJECT = (payload) => store.dispatch('FETCH_PROJECT', payload);
const FETCH_UNIT = (payload) => store.dispatch('FETCH_UNIT', payload);

const toggle_popover = (event) => {
  popover.value.toggle(event);
};

const fetch_filters_records = async () => {
  if (q_decision_type_id.value) await FETCH_DECISION_TYPE({ id: q_decision_type_id.value });
  if (q_project_id.value) await FETCH_PROJECT({ id: q_project_id.value });
  if (q_unit_id.value) await FETCH_UNIT({ id: q_unit_id.value });
};

watch(q_project_id, (value) => {
  if (!value) {
    q_unit_id.value = null;
    q_decision_type_id.value = null;
  }
});

watch(show_extra_filters, (value) => {
  if (!value) q_decision_type_id.value = null;
});

watch(filters, async () => {
  try {
    await fetch_filters_records();
  } catch (error) {
    if (sidebar_type.value !== SIDEBAR_TYPE_NOTIFICATIONS) throw error;
    toast_action_failed({ error });
  }
  UPDATE_SIDEBAR_PREFS(filters.value);
  emit('fetch_notification_subgroups');
});

watch(sidebar_type, async (value) => {
  if (value === SIDEBAR_TYPE_NOTIFICATIONS && !initialized.value) {
    await fetch_filters_records();
    initialized.value = true;
  }
});

onMounted(() => {
  q_project_id.value = sidebar_prefs.value.project_id;
  q_decision_type_id.value = sidebar_prefs.value.decision_type_id;
  q_is_unread_only.value = sidebar_prefs.value.unopen;
  q_unit_id.value = sidebar_prefs.value.unit_id;
});
</script>
